import { useEffect, useState, ChangeEvent, useCallback } from 'react';
import {
  InputAdornment,
  Skeleton,
  Table,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { useGetStormRates } from 'api/rates';
import { StyledButton } from 'components/Buttons';
import { StyledTextField } from 'components/Inputs';
import { StyledTableCell, StyledTableHeadCell } from 'components/Table';
import { useRatesContext } from 'contexts/ratesContext';
import { debounce } from 'lodash';
import { RowData } from 'pages/rates/components/RatesTable/RatesTable.types';
import { useTranslation } from 'react-i18next';
import { IoIosResize } from 'react-icons/io';
import { IoSearch } from 'react-icons/io5';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { reactPaths } from 'root/pathnames';
import { RatesTab, RatesType } from 'root/pathnames/tabs';
import { ConfirmDeleteRateDialog } from '../ConfirmDeleteRateDialog';
import * as S from './SideTable.css';
import { Rate } from './types';

type Props = {
  onRateChange: () => void;
  rate: Rate;
};

export const SideTable = ({ rate, onRateChange }: Props) => {
  const { t } = useTranslation(['misc', 'rates', 'datagrid']);
  const navigate = useNavigate();

  const { tab: rateTab } = useParams<{ tab: RatesTab; rateId: string }>();
  const { getTabParams, getSetTabParams } = useRatesContext();
  const params = getTabParams();
  const setParams = getSetTabParams();

  const [searchValue, setSearchValue] = useState(params.filterText);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { data, loading: ratesLoading, getStormRates } = useGetStormRates();
  const [ratesData, setRatesData] = useState<Array<RowData>>([]);
  const [count, setCount] = useState<number>(0);

  useEffect(() => {
    const fetchRates = async () => {
      const sideTableParams = { ...params, resultSize: 10 };
      await getStormRates(sideTableParams);
    };
    fetchRates();
  }, [params, rate]);

  useEffect(() => {
    if (data) {
      setRatesData(data.data);
      setCount(data.count);
    }
  }, [data]);

  const debounceSearch = useCallback(
    debounce((value: string) => {
      setParams((prev) => ({ ...prev, filterText: value, pageNumber: 1 }));
    }, 500),
    [],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    debounceSearch(e.target.value);
  };

  const handleRedirect = (id: number) => {
    onRateChange();
    navigate(
      generatePath(reactPaths.ratesDetails, {
        ratesType: RatesType.RATES,
        tab: rateTab,
        ratesId: String(id),
      }),
    );
  };

  const handleChangePage = (_: unknown, newPage: number) =>
    setParams((prev) => ({ ...prev, pageNumber: newPage + 1 }));

  return (
    <>
      <ConfirmDeleteRateDialog
        open={isDialogOpen}
        rate={rate}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={() =>
          navigate(
            generatePath(reactPaths.ratesModule, {
              tab: rateTab,
            }),
          )
        }
      />
      <S.SideTableContainer>
        <S.ToolbarContainer>
          <S.ResizeButton
            size='small'
            onClick={() =>
              navigate(
                generatePath(reactPaths.ratesModule, {
                  tab: rateTab,
                }),
              )
            }
          >
            <IoIosResize size='25' />
          </S.ResizeButton>
          <StyledTextField
            placeholder={t('button.search', { ns: 'misc' })}
            type='text'
            size='small'
            padding='dense'
            width={224}
            value={searchValue}
            onChange={handleSearch}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IoSearch />
                </InputAdornment>
              ),
            }}
          />
        </S.ToolbarContainer>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableHeadCell height='condensed'>
                {t('rateNumber', { ns: 'rates' })}
              </StyledTableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ratesLoading
              ? Array.from(new Array(10)).map((_, index) => (
                  <S.InvoicesTableRow key={index}>
                    <StyledTableCell height='condensed' padding='checkbox'>
                      <Skeleton variant='text' width={100} />
                    </StyledTableCell>
                  </S.InvoicesTableRow>
                ))
              : Array.isArray(ratesData) &&
                ratesData.map((i: RowData) => (
                  <S.InvoicesTableRow
                    key={i.id}
                    selected={i.id === Number(rate.id)}
                    hover
                    onClick={() => handleRedirect(i.id)}
                  >
                    <StyledTableCell height='condensed' padding='checkbox'>
                      {i.rateNumber}
                    </StyledTableCell>
                  </S.InvoicesTableRow>
                ))}
          </TableBody>
        </Table>
        {!!count && (
          <TablePagination
            component='div'
            count={count}
            rowsPerPage={10}
            rowsPerPageOptions={[10]}
            page={params.pageNumber - 1}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ from, to, count }) =>
              `${from} - ${to} ${t('of', { ns: 'datagrid' })} ${count}`
            }
          />
        )}
        <S.ActionsContainer>
          <StyledButton
            $primary
            onClick={() =>
              navigate(
                generatePath(reactPaths.ratesForm, {
                  tab: rateTab,
                }),
              )
            }
          >
            {t('button.create', { ns: 'misc' })}
          </StyledButton>
          <StyledButton onClick={() => setIsDialogOpen(true)} $primary disabled={!!rate.publicationDate}>
            {t('button.delete', { ns: 'misc' })}
          </StyledButton>
        </S.ActionsContainer>
      </S.SideTableContainer>
    </>
  );
};
